import Alpine from 'alpinejs'
import { Datepicker } from 'vanillajs-datepicker'
import nl from 'vanillajs-datepicker/locales/nl'
import { initShowStatus } from './show-status'

Object.assign(Datepicker.locales, nl)

type IApiResponse = {
    success: boolean
    shows: string
    filters: {
        dates: { [x: string]: string }
        genres: { id: number; value: string; title: string; disabled: boolean }[]
        tags: { id: number; value: string; title: string; disabled: boolean }[]
        locations: { id: number; value: string; title: string; disabled: boolean }[]
    }
    seo: {
        title: string
        description: string
        genreText: string | undefined
    }
}

const formatDate = (date: Date | null) => {
    if (!date || date.toString() === 'Invalid Date') {
        return ''
    }

    const day = date.getDate().toString().padStart(2, '0')
    const month = (date.getMonth() + 1).toString().padStart(2, '0') // Months are zero-based
    const year = date.getFullYear()

    return `${day}-${month}-${year}`
}

const formatDate2 = (date: Date | null) => {
    if (!date || date.toString() === 'Invalid Date') {
        return ''
    }

    const day = date.getDate().toString().padStart(2, '0')
    const month = (date.getMonth() + 1).toString().padStart(2, '0') // Months are zero-based
    const year = date.getFullYear()

    return `${year}-${month}-${day}`
}

const parseDate = (dateString: string | null) => {
    if (!dateString) {
        return null
    }

    let [day, month, year] = dateString.split('-').map(Number)
    return new Date(year, month - 1, day) // Month is zero-based in JS Date
}

export const initShowOverview = () => {
    Alpine.data('showOverview', () => ({
        filterDate: '',
        filterGenre: '',
        filterTag: '',
        filterLocation: '',
        datePicker: null as Datepicker | null,
        dateElement: null as HTMLInputElement | null,
        debouncedFilter: null as ((queryParams: URLSearchParams) => void) | null,
        init() {
            this.debouncedFilter = Alpine.debounce((qp: URLSearchParams) => {
                this.filter(qp)
            }, 200)

            this.dateElement = document.querySelector('input[name="filterDates"]')

            if (this.dateElement) {
                const getMaxDate = (): Date | undefined => {
                    const availableDates = JSON.parse(this.dateElement?.dataset.availableDates || '{}')
                    const dateKeys = Object.keys(availableDates)

                    if (dateKeys.length === 0) return undefined

                    const maxDateString = dateKeys.sort().pop()
                    return maxDateString ? new Date(maxDateString) : undefined
                }

                this.datePicker = new Datepicker(this.dateElement, {
                    format: 'dd-mm-yyyy',
                    language: 'nl',
                    minDate: new Date(),
                    datesDisabled: (date: Date, viewId: number) => {
                        if (viewId !== 0) return

                        const availableDates = JSON.parse(this.dateElement?.dataset.availableDates || '{}')
                        const cDate = formatDate2(date)

                        return !availableDates[cDate]
                    },
                    maxDate: getMaxDate(),
                })
            }

            document.addEventListener('changeDate', (e: CustomEvent) => {
                this.filterDate = formatDate(new Date(e.detail.date))
            })

            const filterDate = parseDate(new URLSearchParams(window.location.search).get('date'))
            this.filterDate = formatDate(filterDate)

            if (filterDate !== null && new Date(filterDate).toString() !== 'Invalid Date') {
                this.datePicker?.setDate(filterDate)
            }

            const path = window.location.pathname.split('/').slice(1)
            let genre = ''

            if (path[0] === 'de' || path[0] === 'en') {
                if (path[2]) {
                    genre = decodeURIComponent(path[2])
                }
            } else {
                if (path[1]) {
                    genre = decodeURIComponent(path[1])
                }
            }

            this.filterGenre = (genre || new URLSearchParams(window.location.search).get('genre') || '').toLowerCase()
            this.filterTag = new URLSearchParams(window.location.search).get('tag') || ''
            this.filterLocation = new URLSearchParams(window.location.search).get('location') || ''

            this.$watch('filterDate', () => this.updateFilter())
            this.$watch('filterGenre', () => this.updateFilter())
            this.$watch('filterTag', () => this.updateFilter())
            this.$watch('filterLocation', () => this.updateFilter())
        },
        clearFilter(filter: string) {
            switch (filter) {
                case 'date':
                    this.filterDate = ''
                    if (this.dateElement) {
                        this.dateElement.value = ''
                    }
                    this.datePicker?.setDate(null)
                    break
                case 'genre':
                    this.filterGenre = ''
                    break
                case 'tag':
                    this.filterTag = ''
                    break
                case 'location':
                    this.filterLocation = ''
                    break
                default:
                    break
            }
        },
        getFiltersForPagination() {},
        updateFilter() {
            const qp = new URLSearchParams()

            const path = window.location.pathname.split('/').slice(1)
            let baseUrl = ''

            if (path[0] === 'de' || path[0] === 'en') {
                baseUrl = '/' + path.slice(0, 2).join('/')
            } else {
                baseUrl = '/' + path.slice(0, 1).join('/')
            }

            if (this.filterGenre) {
                baseUrl += '/' + this.filterGenre
            }

            if (this.filterDate) {
                qp.set('date', this.filterDate)
            } else {
                qp.delete('date')
            }

            if (this.filterTag) {
                qp.set('tag', this.filterTag)
            } else {
                qp.delete('tag')
            }

            if (this.filterLocation) {
                qp.set('location', this.filterLocation)
            } else {
                qp.delete('location')
            }

            baseUrl = baseUrl.replace('//', '/')

            history.replaceState(null, '', baseUrl + '?' + qp.toString())

            if (this.filterGenre) {
                qp.set('genre', this.filterGenre)
            } else {
                qp.delete('genre')
            }

            this.debouncedFilter?.(qp)
        },
        goToPage(page: string) {
            const query = new URLSearchParams(window.location.search)

            if (query.get('genre') || query.get('tag') || query.get('location') || query.get('date')) {
                query.delete('page')
                location.href = page + '&' + query.toString()
            } else {
                location.href = page
            }
        },
        async filter(queryParams: URLSearchParams) {
            try {
                const path = window.location.pathname.split('/').slice(1)
                let baseUrl = ''

                if (path[0] === 'de') {
                    baseUrl = '/de/actions/_itix-filter/filter/refresh-filters'
                } else if (path[0] === 'en') {
                    baseUrl = '/en/actions/_itix-filter/filter/refresh-filters'
                } else {
                    baseUrl = '/actions/_itix-filter/filter/refresh-filters'
                }

                queryParams.set('slug', window.location.pathname.split('/')[1])
                const urlWithParams = `${baseUrl}?${queryParams.toString()}`

                const response = await fetch(urlWithParams)
                const html = (await response.json()) as IApiResponse

                this.$refs.shows.outerHTML = html.shows

                initShowStatus()

                html.filters.genres.forEach((genre) => {
                    if (genre.disabled) {
                        this.$refs[`genre-${genre.id}`].setAttribute('disabled', 'disabled')
                    } else {
                        this.$refs[`genre-${genre.id}`].removeAttribute('disabled')
                    }
                })

                html.filters.tags.forEach((tag) => {
                    if (tag.disabled) {
                        this.$refs[`tag-${tag.id}`].setAttribute('disabled', 'disabled')
                    } else {
                        this.$refs[`tag-${tag.id}`].removeAttribute('disabled')
                    }
                })

                html.filters.locations.forEach((location) => {
                    if (location.disabled) {
                        this.$refs[`location-${location.id}`].setAttribute('disabled', 'disabled')
                    } else {
                        this.$refs[`location-${location.id}`].removeAttribute('disabled')
                    }
                })

                this.$refs.genreTitle.innerText = html.seo.title
                this.$refs.genreDescription.innerHTML = html.seo.description
                // this.$refs.genreText.innerHTML = html.seo.genreText || ''

                // if (html.seo.genreText) {
                //     this.$refs['genreTextWrapper'].classList.remove('hide')
                // } else {
                //     this.$refs['genreTextWrapper'].classList.add('hide')
                // }

                this.$refs.filterDatesInput.setAttribute('data-available-dates', JSON.stringify(html.filters.dates))
                this.datePicker?.refresh('picker', true)
            } catch (error) {
                console.error(error)
            }
        },
    }))
}
